import { api } from '../api.js';
import { getHomeFarmWarnings } from '../api/homeFarmPreview.js';
import { plantProtectionStatsApi } from '../api/plantProtectionStats.js';
import { iotAccessApi } from '../api/iotAccess.js';

const EMPTY_TOTALS = {
  farms: 0,
  plots: 0,
  devices: 0,
  warnings: 0,
  activeJobs: 0,
  crops: 0
};

const SYSTEM_TENANT_IDS = new Set(['default', 'default-tenant']);

function isBusinessTenant(tenant) {
  const id = String(tenant?.id || '').trim().toLowerCase();
  const code = String(tenant?.tenantCode || tenant?.code || '').trim().toLowerCase();
  if (!id) return false;
  if (SYSTEM_TENANT_IDS.has(id) || SYSTEM_TENANT_IDS.has(code)) return false;
  return true;
}

function parseFarmCount(res) {
  const body = res?.data;
  if (body?.success && Array.isArray(body.data)) return body.data.length;
  if (Array.isArray(body)) return body.length;
  return 0;
}

function parsePlotTotal(res) {
  const body = res?.data;
  if (body?.success) {
    if (body.data?.totalElements != null) return Number(body.data.totalElements) || 0;
    if (Array.isArray(body.data)) return body.data.length;
    if (Array.isArray(body.data?.content)) {
      return Number(body.data.totalElements) || body.data.content.length;
    }
  }
  if (body?.totalElements != null) return Number(body.totalElements) || 0;
  if (Array.isArray(body?.content)) return body.content.length;
  if (Array.isArray(body?.data)) return body.data.length;
  return 0;
}

function parseDeviceOnline(summaryRes, iotRes) {
  let online = 0;
  const summaryBody = summaryRes?.data;
  if (summaryBody?.success && summaryBody.data) {
    online += Number(summaryBody.data.online) || 0;
  } else if (summaryBody && typeof summaryBody.online === 'number') {
    online += summaryBody.online;
  }
  const iotBody = iotRes?.data;
  const list = iotBody?.success && Array.isArray(iotBody.data) ? iotBody.data : [];
  online += list.filter((d) => d?.onlineStatus === true).length;
  return online;
}

function parseWarningCount(warnBody) {
  if (!warnBody?.success || !Array.isArray(warnBody.data)) return 0;
  return warnBody.data.length;
}

function parseUnifiedKpis(statsRes) {
  const body = statsRes?.data;
  const raw = body?.success ? body.data : null;
  const uk = raw?.unifiedKpis || {};
  return {
    activeJobs: Number(uk.inProgressOperations) || 0,
    crops: Number(uk.activeCrops) || 0
  };
}

async function fetchTenantBannerRow(tenant) {
  const tenantId = tenant.id;
  const tenantName = tenant.tenantName || tenant.name || tenantId;
  const settled = await Promise.allSettled([
    api.get('/farms', { params: { tenantId } }),
    api.get('/land-parcels', { params: { tenantId, page: 0, size: 1 } }),
    getHomeFarmWarnings(tenantId),
    plantProtectionStatsApi.getStats({ tenantId, scope: 'farm' }),
    api.get('/farm-smart-device/summary', { params: { tenantId } }),
    iotAccessApi.listDevices(tenantId)
  ]);

  const pick = (idx) => (settled[idx].status === 'fulfilled' ? settled[idx].value : null);
  const farmsRes = pick(0);
  const landRes = pick(1);
  const warnBody = pick(2);
  const statsRes = pick(3);
  const deviceRes = pick(4);
  const iotRes = pick(5);
  const kpis = parseUnifiedKpis(statsRes);

  return {
    tenantId,
    tenantName,
    farms: parseFarmCount(farmsRes),
    plots: parsePlotTotal(landRes),
    devices: parseDeviceOnline(deviceRes, iotRes),
    warnings: parseWarningCount(warnBody),
    activeJobs: kpis.activeJobs,
    crops: kpis.crops
  };
}

/**
 * 平台管理员欢迎页横幅：按租户汇总 KPI，并返回各租户明细行
 */
export async function loadPlatformBannerStats(tenants) {
  const list = (tenants || []).filter(isBusinessTenant);
  if (!list.length) {
    return { totals: { ...EMPTY_TOTALS }, byTenant: [] };
  }

  const byTenant = await Promise.all(list.map(fetchTenantBannerRow));
  const totals = byTenant.reduce(
    (acc, row) => ({
      farms: acc.farms + row.farms,
      plots: acc.plots + row.plots,
      devices: acc.devices + row.devices,
      warnings: acc.warnings + row.warnings,
      activeJobs: acc.activeJobs + row.activeJobs,
      crops: acc.crops + row.crops
    }),
    { ...EMPTY_TOTALS }
  );

  return {
    totals,
    byTenant: byTenant.sort((a, b) => String(a.tenantName).localeCompare(String(b.tenantName), 'zh-CN'))
  };
}

export const PLATFORM_BANNER_METRIC_COLUMNS = [
  { key: 'farms', label: '农场数', unit: '个', icon: '🏠', tone: 'emerald' },
  { key: 'plots', label: '地块数', unit: '块', icon: '🗺️', tone: 'teal' },
  { key: 'devices', label: '在线设备', unit: '台', icon: '🖥️', tone: 'sky' },
  { key: 'warnings', label: '预警数', unit: '条', icon: '🔔', tone: 'amber' },
  { key: 'activeJobs', label: '进行中作业', unit: '项', icon: '🚜', tone: 'blue' },
  { key: 'crops', label: '在种作物', unit: '种', icon: '🌱', tone: 'lime' }
];

const TONE_HEADER = {
  emerald: 'text-emerald-700 dark:text-emerald-300',
  teal: 'text-teal-700 dark:text-teal-300',
  sky: 'text-sky-700 dark:text-sky-300',
  amber: 'text-amber-700 dark:text-amber-300',
  blue: 'text-blue-700 dark:text-blue-300',
  lime: 'text-lime-700 dark:text-lime-300'
};

const TONE_CELL = {
  emerald: 'bg-emerald-50/80 dark:bg-emerald-950/30',
  teal: 'bg-teal-50/80 dark:bg-teal-950/30',
  sky: 'bg-sky-50/80 dark:bg-sky-950/30',
  amber: 'bg-amber-50/80 dark:bg-amber-950/30',
  blue: 'bg-blue-50/80 dark:bg-blue-950/30',
  lime: 'bg-lime-50/80 dark:bg-lime-950/30'
};

export function getPlatformMetricToneClass(key, part = 'header') {
  const col = PLATFORM_BANNER_METRIC_COLUMNS.find((c) => c.key === key);
  const tone = col?.tone || 'emerald';
  return part === 'cell' ? TONE_CELL[tone] : TONE_HEADER[tone];
}
